import type { VenuePreparation } from '@pokerroom/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
import { openVenueSetup } from '../venue-preparation-ui';

const groups = [
  ['administrator'],
  ['tables', 'displays', 'deviceBindings'],
  ['memberDirectory', 'memberPolicies'],
  ['blindStructures', 'tournamentTemplates', 'timedSettlement', 'payoutRules'],
  ['points', 'accounting'],
];

export default function VenuePreparationPanel({
  venueId,
  name,
  onClose,
}: {
  venueId: string;
  name: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('admin');
  const revision = useAuthStore((s) => s.authRevision);
  const client = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const query = useQuery({
    queryKey: ['venue-preparation', venueId, revision],
    queryFn: async ({ signal }) => {
      const data = await api.get<VenuePreparation>(`/venues/${venueId}/onboarding`, { signal });
      if (data.targetVenueId !== venueId) throw new Error('Preparation venue mismatch');
      return data;
    },
    retry: false,
  });
  return (
    <section aria-label={t('venue.preparation')} className="card bg-base-200 mb-6">
      <div className="card-body p-4">
        <div className="flex flex-wrap justify-between gap-2">
          <h3 className="font-semibold">
            {t('venue.preparation')} — {name}
          </h3>
          <div className="flex gap-2">
            <button
              className="btn btn-sm btn-ghost"
              disabled={busy || query.isFetching}
              onClick={() => void query.refetch()}
            >
              {t('venue.refreshPreparation')}
            </button>
            <button className="btn btn-sm btn-ghost" disabled={busy} onClick={onClose}>
              {t('venue.close')}
            </button>
          </div>
        </div>
        <p className="text-sm">{t('venue.preparationHint')}</p>
        {query.isPending && <p>{t('loading')}</p>}
        {query.isError && (
          <p role="alert" className="text-error">
            {t('venue.preparationFailed')}
          </p>
        )}
        {query.data && (
          <>
            <p className="text-sm font-medium">
              {t(`venue.membershipMode.${query.data.membershipMode}`)}
            </p>
            {groups.map((keys, group) => (
              <div key={group} className="mt-3">
                <h4 className="font-semibold mb-2">
                  {group + 1}. {t(`venue.preparationGroup.${group}`)}
                </h4>
                <ul className="space-y-2">
                  {query.data.items
                    .filter((item) => keys.includes(item.key))
                    .map((item) => (
                      <li
                        key={item.key}
                        className="rounded border border-base-300 p-3 flex flex-wrap items-start justify-between gap-3"
                        data-preparation-item={item.key}
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-medium">
                            {t(`venue.preparationItem.${item.key}`)}
                          </span>{' '}
                          <span
                            className={`badge badge-sm ${item.status === 'ready' ? 'badge-success' : item.status === 'missing' ? 'badge-warning' : 'badge-outline'}`}
                          >
                            {t(`venue.preparationStatus.${item.status}`)}
                          </span>
                          <p className="text-xs mt-1">
                            {t(`venue.applicability.${item.applicability}`)}
                          </p>
                          <p className="text-sm mt-1">
                            {t(`venue.preparationReason.${item.reason}`, { count: item.count })}
                          </p>
                        </div>
                        <button
                          className="btn btn-sm btn-outline"
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            setError('');
                            try {
                              await openVenueSetup(venueId, item.page, client, navigate);
                            } catch {
                              setError(t('venue.setupNavigationFailed'));
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          {t('venue.openSetup')}
                        </button>
                      </li>
                    ))}
                </ul>
              </div>
            ))}
            <p className="text-sm border-t border-base-300 pt-3 mt-3">
              {t('venue.openingChecksHint')}
            </p>
          </>
        )}
        {error && (
          <p role="alert" className="text-error">
            {error}
          </p>
        )}
      </div>
    </section>
  );
}
